import { inject, Injectable } from '@angular/core';
import { AuthorizedPersonnelModelKeys, FinancialRequiredModelKeys } from '../const/form-field-key.const';
import { PortalFieldConfig } from '@portal/form/data-access';
import { AuthorizedPersonnelFormConfigService } from './authorized-personnel-form-config.service';
import { debounceTime, distinctUntilChanged, map, startWith, tap } from 'rxjs';
import { PERSONNEL_OTHER } from '../const/app.const';
import { AuthorizedPersonnelOption } from '../type';
import { BeneficialOwnerFormConfigService } from './beneficial-owner-form-config.service';
import { UsPersonFormConfigService } from './us-person-form-config.service';
import { ApplicationStore, PersonnelFacade } from '../store';
import { BoardResolutionFormConfigService } from './board-resolution-form-config.service';

@Injectable({ providedIn: 'root' })
export class FinancialRequiredFormFieldConfigService {
  private readonly applicationStore = inject(ApplicationStore);
  private readonly personnelFacade = inject(PersonnelFacade);
  private readonly authorizedPersonnelFormConfigService = inject(AuthorizedPersonnelFormConfigService);
  private readonly beneficialOwnerFormConfigService = inject(BeneficialOwnerFormConfigService);
  private readonly usPersonFormConfigService = inject(UsPersonFormConfigService);
  private readonly boardResolutionFormConfigService = inject(BoardResolutionFormConfigService);

  getFinancialRequiredFormFields(): PortalFieldConfig[] {
    return [
      {
        fieldGroupClassName: 'field-columns',
        fieldGroup: [
          {
            key: FinancialRequiredModelKeys.product,
            type: 'input',
            defaultValue: null,
            props: {
              label: 'Product Name',
              placeholder: 'Please enter input',
              disabled: true,
            },
          },
          {
            key: FinancialRequiredModelKeys.amountRequired,
            type: 'input',
            defaultValue: null,
            props: {
              label: 'Amount Required',
              placeholder: 'Please enter input',
              disabled: true,
              prefix: 'SGD',
              maskConfig: { mask: 'separator.2' },
            },
          },
          {
            key: FinancialRequiredModelKeys.tenure,
            type: 'number',
            defaultValue: null,
            props: {
              label: 'Tenure',
              placeholder: 'Please enter input',
              suffix: 'Month(s)',
              disabled: true,
            },
          },
        ],
      },
      {
        template: `
          <p class='font-bold portal-paragraph'>KEY PERSONNEL</p>
          <p class="portal-paragraph">Ensure the following details are correct.</p>
        `,
      },
      {
        key: FinancialRequiredModelKeys.keyPersonnels,
        type: 'form-array-authorized-personnel',
        fieldArray: {
          fieldGroupClassName: 'field-columns',
          fieldGroup: this.authorizedPersonnelFormConfigService.initAuthorizedPersonnelFormFields(),
        },
        validators: { validation: ['must-have-authorized-signatory', 'uniqueNricInArray'] },
        asyncValidators: {
          validation: [
            {
              name: 'appointment-async',
              options: { application$: this.applicationStore.application$ },
            },
          ],
        },
        hooks: {
          onInit: field => {
            const control = field.formControl;

            return control?.valueChanges.pipe(
              startWith(control?.value),
              debounceTime(300),
              map(() => control?.getRawValue()),
              distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)),
              tap(value => {
                if (!field.props || !Array.isArray(value)) return;

                const appPersonnelOptions = this.applicationStore.appPersonnelOptionsMap$.getValue();
                const portalPersonnelOptions = Array.from<AuthorizedPersonnelOption>(appPersonnelOptions.values()).map(av => {
                  av.disabled = av.value !== PERSONNEL_OTHER && value.some(v => v[AuthorizedPersonnelModelKeys.uuid] === av.value);
                  return av;
                });

                this.authorizedPersonnelFormConfigService.portalPersonnelOptions$.next(portalPersonnelOptions);

                // const entityIds = value?.map((v: object) => v[AuthorizedPersonnelModelKeys.entityId]).filter(Boolean) ?? [];

                // const personnelOptions =
                //   this.authorizedPersonnelFormConfigService.personnelOptions$.value?.map(option => {
                //     option.disabled = option.value !== PERSONNEL_OTHER && entityIds.includes(option.value);
                //     return option;
                //   }) ?? [];

                //Update personnelOptions base on value
                // Array.from(personnelOptions as unknown as { personnel: AuthorizedPersonnelModel; value: string }[]).forEach(
                //   (person: { personnel: AuthorizedPersonnelModel; value: string }) => {
                //     const updateItem = value.find((item: any) => item.entityId === person.value);
                //     if (updateItem) {
                //       // Update personnel fields based on the updateItem
                //       person.personnel = { ...person.personnel, ...updateItem };
                //     }
                //   },
                // );

                // this.authorizedPersonnelFormConfigService.personnelOptions$.next(personnelOptions);

                // if (control?.status === 'VALID' && Array.isArray(field.model)) {
                // if (field?.options?.formState?.isFinalStep === true) return;
                // this.updateSelectOptionsBasedOnAuthorizedPersonnels({ authorizedPersonnelsModel: field.model });
                // }

                // if (control?.status === 'VALID' && Array.isArray(field.model)) {
                //   console.log('Set Authorized Personnels', field.model);
                //
                //   const options = field.model
                //     .filter(v => v instanceof AuthorizedPersonnelModel)
                //     .map<AuthorizedPersonnelOption>((v: AuthorizedPersonnelModel) => {
                //       return {
                //         label: v.name,
                //         value: v.metadata?.uuid ?? PERSONNEL_OTHER,
                //         personnel: v,
                //         disabled: false,
                //       };
                //     });
                //
                //   if (!field?.options?.formState?.isFinalStep) {
                //     this.authorizedPersonnelFormConfigService.authorizedPersonnelOptions$.next(options);
                //   }
                //
                //   const shareholders = options.filter(option => option.personnel?.appointment?.includes('SHAREHOLDER'));
                //   const authorizedSignatories = options.filter(option => option.personnel?.appointment?.includes('AUTHORISED_SIGNATORY'));
                //
                //   this.beneficialOwnerFormConfigService.authorizedPersonnelOptions$.next(options);
                //   this.boardResolutionFormConfigService.authorizedSignatoryOptions$.next(authorizedSignatories);
                //   this.usPersonFormConfigService.authorizedPersonnelOptions$.next(shareholders);
                //   if (shareholders.length > 0 && this.usPersonFormConfigService.firstPersonnelTrigger$.getValue() === false) {
                //     this.usPersonFormConfigService.firstPersonnelTrigger$.next(true);
                //   }
                // }
              }),
            );
          },
        },
      },
    ];
  }

  // updateSelectOptionsBasedOnAuthorizedPersonnels(args: { authorizedPersonnelsModel: unknown }): void {
  //   if (!args.authorizedPersonnelsModel || !Array.isArray(args.authorizedPersonnelsModel)) return;
  //   console.log('Set Authorized Personnels', args.authorizedPersonnelsModel);
  //
  //   const options = args.authorizedPersonnelsModel
  //     .filter(v => v instanceof AuthorizedPersonnelModel)
  //     .map<AuthorizedPersonnelOption>((v: AuthorizedPersonnelModel) => {
  //       return {
  //         label: v.name,
  //         value: v.metadata?.uuid ?? PERSONNEL_OTHER,
  //         personnel: v,
  //         disabled: false,
  //       };
  //     });
  //
  //   const shareholders = options.filter(option => option.personnel?.appointment?.includes('SHAREHOLDER'));
  //   const authorizedSignatories = options.filter(option => option.personnel?.appointment?.includes('AUTHORISED_SIGNATORY'));
  //
  //   this.authorizedPersonnelFormConfigService.authorizedPersonnelOptions$.next(options); // TODO: find the listener of this
  //   this.beneficialOwnerFormConfigService.authorizedPersonnelOptions$.next(options);
  //   this.boardResolutionFormConfigService.authorizedSignatoryOptions$.next(authorizedSignatories);
  //   this.usPersonFormConfigService.authorizedPersonnelOptions$.next(shareholders);
  //   if (shareholders.length > 0 && this.usPersonFormConfigService.firstPersonnelTrigger$.getValue() === false) {
  //     this.usPersonFormConfigService.firstPersonnelTrigger$.next(true);
  //   }
  // }
}
